import React, { PropsWithChildren } from 'react';
import { Box, Stack, Typography } from '@mui/material';
import { TextBlock } from 'common/ui/TextBlock';
import { Button } from 'system/atoms';
import { CandidateBatch } from 'generated-graphql/types';
import { Loading } from 'common/ui/Loading';
import { OverrideLockCandidatesCheckbox } from './OverrideLockCandidatesCheckbox';
import { formatToFullDate } from 'utils/datetime';

const width = '750px';

type Props = PropsWithChildren<{
  title: string;
  subtitle: string;
  candidateBatch: CandidateBatch;
  loading: boolean;
  isSubmitting?: boolean;
  onCancel: () => void;
  onSubmit?: () => void;
  overrideCheckedValue?: boolean;
  handleOverrideChecked?: (value: boolean) => void;
  additionalComponents?: React.ReactNode;
  overrideActionButtons?: React.ReactNode;
  showLastUpdate?: boolean;
  updateButtonLabel?: string;
}>;

export const AutomationDrawerContainer: React.FC<Props> = ({
  candidateBatch,
  title,
  subtitle,
  loading,
  children,
  isSubmitting,
  onSubmit,
  onCancel,
  overrideCheckedValue,
  handleOverrideChecked,
  additionalComponents,
  overrideActionButtons,
  showLastUpdate,
  updateButtonLabel
}) => {
  if (loading) {
    return (
      <Loading
        containerSx={{
          width
        }}
      />
    );
  }

  return (
    <Stack
      direction="column"
      sx={{
        width
      }}
      gap="40px"
    >
      <Stack direction="column" spacing={2}>
        <TextBlock variant="drawer" title={title} subtitle={subtitle} />

        {showLastUpdate && (
          <Typography sx={{ color: 'text.secondary' }}>
            Last actioned {formatToFullDate(candidateBatch.updatedAt)}
          </Typography>
        )}
      </Stack>

      <Box>{children}</Box>

      {overrideCheckedValue !== undefined && (
        <OverrideLockCandidatesCheckbox
          value={overrideCheckedValue}
          onChange={(value) => handleOverrideChecked?.(value)}
        />
      )}
      {additionalComponents && <>{additionalComponents}</>}

      <Stack direction="row" gap={2}>
        {overrideActionButtons && <>{overrideActionButtons}</>}
        {!overrideActionButtons && onSubmit && (
          <Button
            variant="primary"
            onClick={onSubmit}
            disabled={isSubmitting}
            loading={isSubmitting}
          >
            {updateButtonLabel || 'Update'}
          </Button>
        )}
        <Button variant="tertiary" onClick={onCancel}>
          Cancel
        </Button>
      </Stack>
    </Stack>
  );
};
